/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React, { useCallback, useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { SalesDashboardChartTooltip } from "./SalesDashboardChartTooltip";
import {
  chartMargin,
  commonStyles,
  createEmptyChartRow,
  createTicketNames,
  getChartColor,
} from "./chartHelper";

import type { ChartRow } from "./chartHelper";
import type {
  GetTicketSalesByOrderDateResponse,
  TicketSalesByOrderDate,
} from "./useGetTicketSalesByOrderDate";

export const TicketSalesByOrderDateCumulativeDashboard: React.FC<{
  data: GetTicketSalesByOrderDateResponse | undefined;
  isLoading: boolean;
}> = ({ data, isLoading }) => {
  const ticketNames = useMemo(() => {
    if (!data) return [];
    return createTicketNames(data.ticketSalesByOrderDate);
  }, [data]);

  const chartData = useMemo(() => {
    if (!data) return undefined;
    return createCumulativeChartData(data.ticketSalesByOrderDate, ticketNames);
  }, [data, ticketNames]);

  const getTooltipSeriesColor = useCallback(
    (seriesName: string) => {
      const index = ticketNames.indexOf(seriesName);

      return index === -1 ? undefined : getChartColor(index);
    },
    [ticketNames]
  );

  if (isLoading || !chartData || chartData.length === 0) {
    return (
      <section css={commonStyles.section}>
        <div css={commonStyles.header}>
          <h4 css={commonStyles.title}>累計販売数（券種別）</h4>
          <p css={commonStyles.subtitle}>
            支払日ごとの販売数を積み上げた累計値
          </p>
        </div>

        {isLoading ? (
          <div
            css={commonStyles.chartArea}
            aria-busy="true"
            aria-label="グラフを読み込み中"
          >
            <div css={commonStyles.skeleton} />
          </div>
        ) : (
          <div css={commonStyles.chartArea}>
            <span css={commonStyles.notDataText}>データがありません</span>
          </div>
        )}
      </section>
    );
  }

  return (
    <section css={commonStyles.section}>
      <div css={commonStyles.header}>
        <h4 css={commonStyles.title}>累計販売数（券種別）</h4>
        <p css={commonStyles.subtitle}>支払日ごとの販売数を積み上げた累計値</p>
      </div>

      <ul css={commonStyles.legend}>
        {ticketNames.map((name, index) => (
          <li css={commonStyles.legendItem} key={name}>
            <span css={styles.legendMarker(getChartColor(index))} />
            <span>{name}</span>
          </li>
        ))}
      </ul>

      <div css={commonStyles.chartArea}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={chartMargin}>
            <CartesianGrid stroke="#2e3d4c" />
            <XAxis
              axisLine={{ stroke: "#8b96a2" }}
              dataKey="label"
              label={{
                fill: "#8b96a2",
                fontSize: 12,
                position: "bottom",
                offset: 6,
                value: "支払日",
              }}
              minTickGap={18}
              tick={{ fill: "#8b96a2", fontSize: 12 }}
              tickLine={false}
              tickMargin={8}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              label={{
                angle: -90,
                fill: "#8b96a2",
                fontSize: 12,
                position: "left",
                offset: 6,
                value: "累計販売数",
              }}
              tick={{ fill: "#8b96a2", fontSize: 12 }}
              tickCount={5}
              tickLine={false}
              width={42}
            />
            <Tooltip
              cursor={{ fill: "rgba(31, 41, 55, 0.04)" }}
              content={
                <SalesDashboardChartTooltip
                  getSeriesColor={getTooltipSeriesColor}
                />
              }
            />
            {ticketNames.map((name, index) => (
              <Line
                key={name}
                type="linear"
                dataKey={name}
                stroke={getChartColor(index)}
                strokeWidth={1.4}
                dot={{ fill: getChartColor(index), r: 1.75 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

const createCumulativeChartData = (
  ticketSalesByOrderDate: TicketSalesByOrderDate[],
  ticketNames: string[]
): ChartRow[] => {
  const rowsByOrderDate = new Map<number, ChartRow>();

  ticketSalesByOrderDate.forEach(({ orderDate, quantity, ticketName }) => {
    const orderDateMilliseconds = Number(orderDate);
    const row =
      rowsByOrderDate.get(orderDateMilliseconds) ??
      createEmptyChartRow(orderDateMilliseconds, ticketNames);

    row[ticketName] = Number(row[ticketName] ?? 0) + quantity;
    rowsByOrderDate.set(orderDateMilliseconds, row);
  });

  const sortedEntries = Array.from(rowsByOrderDate.entries()).sort(
    ([a], [b]) => a - b
  );

  const cumulativeRows: ChartRow[] = [];
  const cumulativeTotals = new Map<string, number>();
  ticketNames.forEach((name) => cumulativeTotals.set(name, 0));

  sortedEntries.forEach(([, row]) => {
    const cumulativeRow: ChartRow = {
      label: String(row.label),
    };

    ticketNames.forEach((ticketName) => {
      const dailyValue = Number(row[ticketName] ?? 0);
      const total = (cumulativeTotals.get(ticketName) ?? 0) + dailyValue;
      cumulativeTotals.set(ticketName, total);
      cumulativeRow[ticketName] = total;
    });

    cumulativeRows.push(cumulativeRow);
  });

  return cumulativeRows;
};

const styles = {
  legendMarker: (color: string) => css`
    width: 9px;
    height: 9px;
    background: ${color};
    border-radius: 50%;
  `,
};
